/**
 * CelestialMessenger - The Oracle's Welcome Component
 * 
 * Displays:
 * - Personalized welcome message based on user's sign
 * - 3 upcoming celestial events with "Enter Chamber" links
 * - Luxury mystical UI with pulsing gold borders
 * - Moon phase-based pulse animation speed
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Star, Moon, Sun, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getAuthHeaders } from '@/utils/api';

// Moon phase energy levels (affects pulse speed)
const MOON_PHASE_ENERGY = {
    'new_moon': { speed: 3, energy: 'low' },
    'waxing_crescent': { speed: 2.5, energy: 'building' },
    'first_quarter': { speed: 2, energy: 'medium' },
    'waxing_gibbous': { speed: 1.5, energy: 'high' },
    'full_moon': { speed: 1, energy: 'peak' },
    'waning_gibbous': { speed: 1.5, energy: 'high' },
    'last_quarter': { speed: 2, energy: 'medium' },
    'waning_crescent': { speed: 2.5, energy: 'low' },
};

// Calculate current moon phase (simplified)
const getCurrentMoonPhase = () => {
    const LUNAR_CYCLE = 29.53059; // days
    const KNOWN_NEW_MOON = new Date('2024-01-11'); // Known new moon date
    const now = new Date();
    const daysSinceNew = (now - KNOWN_NEW_MOON) / (1000 * 60 * 60 * 24);
    const lunarAge = daysSinceNew % LUNAR_CYCLE;
    
    if (lunarAge < 1.85) return 'new_moon';
    if (lunarAge < 7.38) return 'waxing_crescent';
    if (lunarAge < 9.23) return 'first_quarter';
    if (lunarAge < 14.77) return 'waxing_gibbous';
    if (lunarAge < 16.61) return 'full_moon';
    if (lunarAge < 22.15) return 'waning_gibbous';
    if (lunarAge < 23.99) return 'last_quarter';
    return 'waning_crescent';
};

// Luxury Chamber Button with gold pulse
const ChamberButton = ({ onClick, children, oracleType = 'cosmos', pulseSpeed = 2 }) => {
    return (
        <button
            onClick={onClick}
            className="group relative overflow-hidden"
            data-testid={`chamber-btn-${oracleType}`}
        >
            {/* Animated border container */}
            <span 
                className="absolute inset-0 rounded-lg"
                style={{
                    background: `linear-gradient(90deg, #E0AA3E, #5D4037, #E0AA3E, #5D4037)`,
                    backgroundSize: '300% 100%',
                    animation: `goldShimmer ${pulseSpeed}s linear infinite`,
                    padding: '1px',
                }}
            >
                <span className="absolute inset-[1px] rounded-lg bg-[#0B0D17]" />
            </span>
            
            {/* Pulsing glow effect */}
            <span 
                className="absolute inset-0 rounded-lg opacity-50"
                style={{
                    boxShadow: `0 0 15px #E0AA3E40, inset 0 0 15px #E0AA3E10`,
                    animation: `astralPulse ${pulseSpeed}s ease-in-out infinite`,
                }}
            />
            
            {/* Button content */}
            <span className="relative flex items-center gap-2 px-4 py-2 text-amber-400/90 text-xs font-inter">
                <Sparkles className="w-3 h-3" />
                {children}
                <ArrowRight className="w-3 h-3 transform group-hover:translate-x-1 transition-transform" />
            </span>
            
            {/* CSS for animations */}
            <style jsx>{`
                @keyframes goldShimmer {
                    0% { background-position: 0% 50%; }
                    100% { background-position: 300% 50%; }
                }
                @keyframes astralPulse {
                    0%, 100% { 
                        box-shadow: 0 0 5px #E0AA3E40, inset 0 0 5px #E0AA3E10;
                        opacity: 0.3;
                    }
                    50% { 
                        box-shadow: 0 0 20px #E0AA3E60, inset 0 0 20px #E0AA3E20;
                        opacity: 0.7;
                    }
                }
            `}</style>
        </button>
    );
};

const CelestialMessenger = ({ 
    userName, 
    sunSign, 
    gender,
    onEnterChamber,
    className = "" 
}) => {
    const { language } = useLanguage();
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [greeting, setGreeting] = useState("");
    const [moonPhase, setMoonPhase] = useState('full_moon');
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    
    const isSpanish = language === 'es';
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
    // Get current moon phase on mount
    useEffect(() => {
        const phase = getCurrentMoonPhase();
        setMoonPhase(phase);
    }, []);
    
    // Get pulse speed based on moon phase
    const pulseSpeed = MOON_PHASE_ENERGY[moonPhase]?.speed || 2;
    const energyLevel = MOON_PHASE_ENERGY[moonPhase]?.energy || 'medium';
    
    // Generate personalized greeting based on gender
    useEffect(() => {
        const name = userName || (isSpanish ? "Viajero" : "Traveler");
        const sign = sunSign ? sunSign.charAt(0).toUpperCase() + sunSign.slice(1) : null;
        
        // Gender-specific welcome prefix
        let welcomePrefix;
        if (isSpanish) {
            if (gender === 'male') {
                welcomePrefix = `Bienvenido de vuelta al Templo, ${name}`;
            } else if (gender === 'female') {
                welcomePrefix = `Bienvenida de vuelta al Templo, ${name}`;
            } else {
                // "other" - just use their name
                welcomePrefix = `${name}, de vuelta al Templo`;
            }
        } else {
            welcomePrefix = `Welcome back to the Temple, ${name}`;
        }
        
        if (isSpanish) {
            if (sign) {
                setGreeting(`${welcomePrefix}. Las estrellas se han movido desde nuestra última conversación, y como ${sign}, una nueva alineación demanda tu atención.`);
            } else {
                setGreeting(`${welcomePrefix}. Las estrellas se han movido desde nuestra última conversación, y una nueva alineación demanda tu atención.`);
            }
        } else {
            if (sign) {
                setGreeting(`${welcomePrefix}. The stars have shifted since we last spoke, and as a ${sign}, a new alignment beckons your attention.`);
            } else {
                setGreeting(`${welcomePrefix}. The stars have shifted since we last spoke, and a new alignment beckons your attention.`);
            }
        }
    }, [userName, sunSign, gender, isSpanish]);
    
    // Fetch celestial events only when expanded (first time)
    useEffect(() => {
        if (!isExpanded || hasFetched) return;
        
        const fetchEvents = async () => {
            setIsLoading(true);
            setHasFetched(true);
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${backendUrl}/api/celestial/events?count=3`, {
                    credentials: 'include',
                    headers
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setEvents(data.events || []);
                }
            } catch (err) {
                console.error('Failed to fetch celestial events:', err);
            }
            setIsLoading(false);
        };
        
        fetchEvents();
    }, [isExpanded, hasFetched, backendUrl]);
    
    // Format date nicely
    const formatDate = (dateStr) => {
        try {
            const date = new Date(dateStr);
            const options = { month: 'short', day: 'numeric' };
            return date.toLocaleDateString(isSpanish ? 'es-MX' : 'en-US', options);
        } catch {
            return dateStr;
        }
    };
    
    // Get icon for event type
    const getEventIcon = (eventType) => {
        switch (eventType) {
            case 'eclipse':
                return <Moon className="w-4 h-4" />;
            case 'ingress':
                return <Star className="w-4 h-4" />;
            case 'conjunction':
                return <Sparkles className="w-4 h-4" />;
            default:
                return <Sun className="w-4 h-4" />;
        }
    };
    
    // Get CTA text based on oracle type
    const getCtaText = (oracleType) => {
        if (isSpanish) {
            switch (oracleType) {
                case 'love':
                    return 'Entrar a la Cámara del Amor';
                case 'dream':
                    return 'Entrar al Reino de los Sueños';
                default:
                    return 'Consultar al Cosmos';
            }
        } else {
            switch (oracleType) {
                case 'love':
                    return "Enter the Love Oracle's Chamber";
                case 'dream':
                    return "Enter the Dream Oracle's Realm";
                default:
                    return 'Consult the Cosmos';
            }
        }
    };
    
    // Get moon phase display name
    const getMoonPhaseName = () => {
        const names = {
            'new_moon': isSpanish ? 'Luna Nueva' : 'New Moon',
            'waxing_crescent': isSpanish ? 'Creciente' : 'Waxing Crescent',
            'first_quarter': isSpanish ? 'Cuarto Creciente' : 'First Quarter',
            'waxing_gibbous': isSpanish ? 'Gibosa Creciente' : 'Waxing Gibbous',
            'full_moon': isSpanish ? 'Luna Llena' : 'Full Moon',
            'waning_gibbous': isSpanish ? 'Gibosa Menguante' : 'Waning Gibbous',
            'last_quarter': isSpanish ? 'Cuarto Menguante' : 'Last Quarter',
            'waning_crescent': isSpanish ? 'Menguante' : 'Waning Crescent',
        };
        return names[moonPhase] || moonPhase;
    };
    
    return (
        <motion.div
            className={`bg-gradient-to-b from-gray-900/80 to-black/90 border border-primary/20 rounded-2xl p-6 ${className}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
        >
            {/* Collapsible Header - Click to expand */}
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full text-left group"
            >
                <div className="flex items-center gap-4 mb-2">
                    <div className="relative">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500/30 to-primary/30 flex items-center justify-center">
                            <Sparkles className="w-7 h-7 text-amber-400" />
                        </div>
                        {/* Pulsing glow effect - speed based on moon phase */}
                        <motion.div 
                            className="absolute inset-0 rounded-full bg-amber-500/20"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.2, 0.5] }}
                            transition={{ duration: pulseSpeed, repeat: Infinity }}
                        />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-primary font-cinzel text-lg group-hover:text-primary/80 transition-colors flex items-center gap-2">
                            {isSpanish ? 'El Oráculo' : 'The Oracle'}
                            <motion.span
                                animate={{ rotate: isExpanded ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                                className="text-primary/40"
                            >
                                ▾
                            </motion.span>
                        </h3>
                        <p className="text-primary/50 text-xs font-inter">
                            {isSpanish ? 'Mensajero Celestial' : 'Celestial Messenger'}
                        </p>
                    </div>
                    
                    {/* Moon Phase Indicator */}
                    <div className="text-right">
                        <div className="text-primary/40 text-[10px] font-mono uppercase">
                            {getMoonPhaseName()}
                        </div>
                        <div className="text-primary/30 text-[9px] font-mono">
                            {isSpanish ? `Energía: ${energyLevel}` : `Energy: ${energyLevel}`}
                        </div>
                    </div>
                </div>
                
                {/* Tap to reveal hint - only when collapsed */}
                {!isExpanded && (
                    <p className="text-primary/30 text-[10px] font-inter text-center mt-2">
                        {isSpanish ? 'Toca para revelar los próximos eventos cósmicos' : 'Tap to reveal upcoming cosmic events'}
                    </p>
                )}
            </button>
            
            {/* Expandable Content */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        {/* Greeting Message */}
                        <p className="text-primary/80 text-sm font-inter leading-relaxed my-4">
                            {greeting}
                        </p>
                        
                        {/* Celestial Events */}
                        {isLoading ? (
                            <div className="flex items-center justify-center py-6">
                                <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            </div>
                        ) : events.length > 0 ? (
                            <div className="space-y-4">
                                <h4 className="text-primary/60 text-xs font-inter uppercase tracking-wider">
                                    {isSpanish ? 'Próximos Eventos Cósmicos' : 'Upcoming Cosmic Events'}
                                </h4>
                                
                                {events.map((event, index) => (
                                    <motion.div
                                        key={event.date + event.event}
                                        className="group"
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.15 }}
                                    >
                                        <div className="bg-black/40 border border-primary/10 rounded-xl p-4 hover:border-primary/30 transition-all">
                                            {/* Event Header */}
                                            <div className="flex items-start gap-3 mb-2">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary/60">
                                                    {getEventIcon(event.type)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-amber-400/80 text-xs font-inter font-medium">
                                                            {formatDate(event.date)}
                                                        </span>
                                                        <span className="text-primary text-sm font-cinzel truncate">
                                                            {event.event}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Event Description */}
                                            <p className="text-primary/60 text-xs font-inter leading-relaxed mb-3 pl-11">
                                                {event.personalized_message || event.mystical_message || event.short_description}
                                            </p>
                                            
                                            {/* Luxury CTA Button with moon-phase pulse */}
                                            <div className="ml-11">
                                                <ChamberButton
                                                    onClick={() => onEnterChamber?.(event.oracle_type || 'cosmos', event)}
                                                    oracleType={event.oracle_type || 'cosmos'}
                                                    pulseSpeed={pulseSpeed}
                                                >
                                                    {getCtaText(event.oracle_type)}
                                                </ChamberButton>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-primary/40 text-sm text-center py-4">
                                {isSpanish ? 'Los cielos están en calma...' : 'The heavens are calm...'}
                            </p>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default CelestialMessenger;
